/**
 * Lista de comprobantes pendientes de trazar — datos REALES desde el ERP ACSIS.
 *
 * Pega a GET /api/despacho/pendientes (backend everex-traza-back), que hace el
 * polling de `tareas.json?tipo=TAE&solopendientes=1` en Plataforma y clasifica
 * cada tarea por `claseComprobante`:
 *   75 → Remito de venta (RT) · 110 → Destrucción (RD) · 112 → Transferencia (TRS)
 *
 * Por ahora los cards son informativos (listado). Abrir el detalle del
 * comprobante para ejecutar la traza requiere los endpoints /obtener/... de
 * ACSIS (todavía no habilitados para medicamentos trazables — ver doc de
 * integración).
 */

import { useMemo, useState } from 'react';
import { Loader2, FileText, RefreshCw, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTareasPendientes, useLastErpTaskSync } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { TareaPendiente } from '@/api/contracts/tareas';

/** Color del chip según el movimiento (por prefijo de referencia): salida en
 *  azul, destrucción en rojo. Default 'salida' si no vino el campo. */
const BADGE_BY_MOVIMIENTO: Record<'salida' | 'destruccion', string> = {
  salida: 'badge-blue',
  destruccion: 'badge-red',
};

/** ISO → "dd/mm/aaaa HH:MM". '' si no hay valor. */
function fmtLastSync(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mo}/${yy} ${hh}:${mm}`;
}

/** "2026-05-27 00:00:00.000" → "27/05/2026". */
function fmtFechaAcsis(value: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : value;
}

interface PendientesTareasListProps {
  onAbrirTarea?: (tarea: TareaPendiente) => void;
  /** Filtro por tipo (sigla) controlado desde el padre — para que persista al
   *  entrar/volver del detalle. Si no se pasan, usa estado local. */
  siglaFiltro?: string | null;
  onSiglaFiltroChange?: (sigla: string | null) => void;
}

export function PendientesTareasList({
  onAbrirTarea,
  siglaFiltro: siglaFiltroProp,
  onSiglaFiltroChange,
}: PendientesTareasListProps = {}) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: tareas, isLoading, error, refetch, isFetching } =
    useTareasPendientes(ubicacionActivaId);
  // Última sincronización exitosa del GLN — leída de audit_log.
  const { data: lastSync } = useLastErpTaskSync(ubicacionActivaId);
  const lastSyncLabel = fmtLastSync(lastSync?.at ?? null);

  // Dos estados: lo que se está tipeando vs lo que se aplicó al apretar Buscar.
  // Mantiene la UX consistente con /recepción (submit explícito) aunque el filtro
  // es client-side.
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [queryAplicada, setQueryAplicada] = useState('');
  // Filtro por tipo (sigla de la referencia: RFA/RES/RCO/DES…). null = todos.
  // Controlado desde el padre si vienen las props (para que persista al volver
  // del detalle); si no, estado local.
  const [siglaFiltroLocal, setSiglaFiltroLocal] = useState<string | null>(null);
  const siglaFiltro = siglaFiltroProp !== undefined ? siglaFiltroProp : siglaFiltroLocal;
  const setSiglaFiltro = onSiglaFiltroChange ?? setSiglaFiltroLocal;

  // Siglas presentes en el listado + cuántas de cada una — arma el filtro
  // dinámico "por tipo" (solo con lo que realmente hay).
  const siglasDisponibles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of tareas ?? []) {
      if (!t.sigla) continue;
      counts.set(t.sigla, (counts.get(t.sigla) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [tareas]);

  const tareasFiltradas = useMemo(() => {
    const q = queryAplicada.trim().toLowerCase();
    return (tareas ?? []).filter((t) => {
      if (siglaFiltro && t.sigla !== siglaFiltro) return false;
      if (!q) return true;
      const remito = (t.numeroRemito ?? '').toLowerCase();
      const ref = (t.referencia ?? '').toLowerCase();
      return remito.includes(q) || ref.includes(q);
    });
  }, [tareas, queryAplicada, siglaFiltro]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Consultando tareas pendientes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No se pudieron obtener las tareas del ERP:{' '}
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  const items = tareas ?? [];
  const itemsFiltrados = tareasFiltradas;
  const filtrando = queryAplicada.trim().length > 0 || siglaFiltro !== null;

  return (
    <div className="space-y-4">
      {items.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQueryAplicada(textoBusqueda);
              }}
              className="flex flex-wrap items-center gap-2"
            >
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={textoBusqueda}
                  onChange={(e) => setTextoBusqueda(e.target.value)}
                  placeholder="Buscar por nº remito o referencia"
                  className="pl-9 font-mono"
                />
              </div>
              <Button
                type="submit"
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              >
                <Search className="h-4 w-4" />
                <span className="ml-1.5">Buscar</span>
              </Button>
              {queryAplicada.trim().length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setTextoBusqueda('');
                    setQueryAplicada('');
                  }}
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </form>

            {/* Filtro por tipo — chips dinámicos según las siglas presentes. */}
            {siglasDisponibles.length > 1 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Tipo
                </span>
                <TipoChip
                  label={`Todos (${items.length})`}
                  active={siglaFiltro === null}
                  onClick={() => setSiglaFiltro(null)}
                />
                {siglasDisponibles.map(([sig, count]) => (
                  <TipoChip
                    key={sig}
                    label={`${sig} (${count})`}
                    active={siglaFiltro === sig}
                    onClick={() => setSiglaFiltro(sig)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-600">
          {filtrando ? (
            <>
              {itemsFiltrados.length} de {items.length} comprobante
              {items.length === 1 ? '' : 's'}
            </>
          ) : (
            <>
              {items.length} comprobante{items.length === 1 ? '' : 's'} pendiente
              {items.length === 1 ? '' : 's'} de trazar (ERP)
            </>
          )}
          {lastSyncLabel && (
            <>
              <span className="mx-2 text-gray-300">·</span>
              <span title="Última sincronización exitosa (audit_log)">
                Última sincronización: {lastSyncLabel}
              </span>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition hover:border-everex-naranja hover:text-everex-azul disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Actualizar
        </button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-everex-azul">Todo al día</h3>
            <p className="mt-1 text-xs text-gray-600">
              No hay comprobantes pendientes de trazar en el ERP.
            </p>
          </CardContent>
        </Card>
      ) : itemsFiltrados.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-gray-500">
            Ningún comprobante coincide con el filtro aplicado.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {itemsFiltrados.map((t, i) => (
            <TareaCard
              key={t.id}
              tarea={t}
              staggerIndex={i}
              onClick={onAbrirTarea ? () => onAbrirTarea(t) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Pill del filtro por tipo (sigla). */
function TipoChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition',
        active
          ? 'border-everex-azul bg-everex-azul text-white'
          : 'border-gray-200 text-gray-600 hover:border-everex-naranja hover:text-everex-azul',
      )}
    >
      {label}
    </button>
  );
}

/**
 * Badge de estado de la última traza intentada sobre esta tarea.
 * Reemplaza al "Por trazar" estático.
 */
function EstadoTrazaBadge({ tarea }: { tarea: TareaPendiente }) {
  const traza = tarea.traza;
  if (!traza) {
    return <span className="badge badge-yellow">Por trazar</span>;
  }
  if (traza.status === 'pending' || traza.status === 'running') {
    return (
      <span
        className="badge badge-blue"
        title={`Job ${traza.jobId} en proceso (riga-traza-v2)`}
      >
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
        Trazando
      </span>
    );
  }
  if (traza.status === 'error' || traza.status === 'failed') {
    return (
      <span
        className="badge badge-red"
        title={traza.errorMessage ?? `Job ${traza.jobId} en error`}
      >
        Error de traza
      </span>
    );
  }
  if (traza.status === 'anulada') {
    return (
      <span className="badge badge-gray" title={`Job ${traza.jobId}: traza cancelada en ANMAT`}>
        Traza cancelada
      </span>
    );
  }
  // 'success' no debería llegar acá — el backend filtra esos. Defensive.
  return <span className="badge badge-green">Trazado</span>;
}

function TareaCard({
  tarea,
  staggerIndex,
  onClick,
}: {
  tarea: TareaPendiente;
  staggerIndex?: number;
  onClick?: () => void;
}) {
  const dataStagger = staggerIndex !== undefined ? Math.min(staggerIndex, 11) : undefined;
  const clickable = Boolean(onClick);
  const conError =
    tarea.traza?.status === 'error' || tarea.traza?.status === 'failed';

  return (
    <Card
      onClick={onClick}
      data-stagger={dataStagger}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        dataStagger !== undefined && 'stagger-item',
        clickable && 'cursor-pointer transition hover:shadow-md',
        // Tareas con traza en error → toda la card teñida en rojo claro para
        // que se destaque en el listado. Hover refuerza el borde.
        conError
          ? 'border-red-300 bg-red-50 hover:border-red-500'
          : clickable && 'hover:border-everex-naranja',
      )}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span
                className={cn('badge', BADGE_BY_MOVIMIENTO[tarea.movimiento ?? 'salida'])}
                title={tarea.tipoLabel}
              >
                <b>{tarea.sigla}</b>
              </span>
              {tarea.numeroRemito ? (
                <span
                  className="text-sm font-bold text-everex-azul"
                  title={`Referencia ERP: ${tarea.referencia ?? '—'}`}
                >
                  {tarea.numeroRemito}
                </span>
              ) : tarea.referencia ? (
                <span className="font-mono text-sm font-bold text-everex-azul">
                  {tarea.referencia}
                </span>
              ) : null}
            </div>

            {tarea.asunto && <div className="mt-1 text-xs text-gray-700">{tarea.asunto}</div>}

            <div className="mt-1 text-xs text-gray-600">
              Solicitante: <b>{tarea.nombreSolicitante ?? '—'}</b>
              {tarea.solicitante ? ` (${tarea.solicitante})` : ''}
            </div>

            {tarea.numeroRemito && tarea.referencia && (
              <div className="mt-1 text-xs text-gray-600" title="Referencia ERP">
                Referencia: <b>{tarea.referencia}</b>
              </div>
            )}
          </div>

          <div className="text-right">
            <EstadoTrazaBadge tarea={tarea} />
            <div className="mt-1 text-xs text-gray-600">{fmtFechaAcsis(tarea.fechaCreacion)}</div>
            {tarea.prioridad && (
              <div className="mt-1 text-[11px] text-gray-500">Prioridad {tarea.prioridad}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
